import React, { useEffect, useState } from 'react';

// constants
import { ACTIONS_LIST, OPERATIONS_LIST } from '../../../constants/triggers';

// helpers
import { cn } from '../../../../../helper/cn';

// components
import TriggerListContainer from './TriggerListContainer';
import { useSelector } from 'react-redux';
import { useFunction } from '../../../../../hooks/useFeatureGating';
import { FUNCTIONS } from '../../../../../constant/featureSlugs';

const tabsList = ['actions', 'operations'];

export default function TriggerModel({
  type = 'list',
  open,
  setOpen,
  position = { top: 0, left: 0 },
  onTriggerClick,
  allow = true,
  previousNodeType,
  findPreviousNode,
  edgeFrom,
  node,
  edges,
  nodes
}) {

  // Get the node type from the previous node
  const nodeType = previousNodeType?.data?.trigger_type || previousNodeType?.data?.type || previousNodeType?.type;
  const template_type = previousNodeType?.data?.subcategory_type || previousNodeType?.data?.sub_category;
  let isFlow = false;
  if (previousNodeType?.data?.type === 'template') {
    const template = previousNodeType?.data?.template;
    const buttonsSection = template?.find(item => item.type === 'BUTTONS');
    const buttons = buttonsSection?.buttons || [];
    isFlow = buttons.some(button => button.type === 'FLOW');
    } else {
    isFlow = previousNodeType?.component_type === 'FLOW';
  }

  const isAfterApiTrigger = nodeType?.toLowerCase() === 'api_trigger';
  const isAfterNextSteps = edgeFrom === 'next_steps';

  const previousNode = node ? findPreviousNode(node) : undefined;
  const previousNodeId = node ? previousNode?.uniq_id : undefined;
  const connectedEdges = Array.isArray(edges) && previousNodeId
    ? edges.filter(edge => edge?.start_id === previousNodeId)
    : [];
  const connectedNodeIds = new Set(connectedEdges.map(edge => edge?.end_id));
  const connectedNodes = previousNodeId && Array.isArray(nodes)
    ? nodes.filter(item => connectedNodeIds.has(item?.uniq_id))
    : [];
  const connectedNodeLabels = previousNodeId
    ? connectedNodes
        .map(item => item?.label || item?.data?.label)
        .filter(Boolean)
    : [];

  // Set default tab based on edgeFrom
  const [selectedTab, setSelectedTab] = useState((edgeFrom !== 'next_steps' || nodeType?.toLowerCase() === 'follow_up') ? 'actions' : 'operations');

  const onTabChange = (tab) => setSelectedTab(tab);

  const { featureFlags } = useSelector((state) => state?.app);

  const { isEnabled: canUseAutoAssign } = useFunction(FUNCTIONS.SEQUENCE_AUTO_ASSIGN);
  const { isEnabled: canUseTimeDelay } = useFunction(FUNCTIONS.SEQUENCE_TIME_DELAY);
  const { limit: messageTypesLimit } = useFunction(FUNCTIONS.SEQUENCE_MESSAGE_TYPES);

  // Operation handlers for different node types
  // Helper function to filter operations based on template type
  const filterOperations = (operations, isAfterNextSteps) => {
    const paymentMatches = isAfterNextSteps && connectedNodeLabels.length > 0
      ? connectedNodeLabels.filter(label =>
          ['Payment Captured', 'Payment Failed'].includes(label)
        )
      : [];
    const blockedPaymentLabels = paymentMatches.length > 0 ? new Set(paymentMatches) : null;

    const flowMatches = isAfterNextSteps && connectedNodeLabels.length > 0
      ? connectedNodeLabels.filter(label =>
          ['Flow Completed'].includes(label)
        )
      : [];
    const blockedFlowLabels = flowMatches.length > 0 ? new Set(flowMatches) : null;

    return operations.filter(item => {
      if (blockedPaymentLabels?.has(item?.label)) {
        return false;
      }
      if (blockedFlowLabels?.has(item?.label)) {
        return false;
      }
      // Always filter out FOLLOW_UP if not after next steps
      if (!isAfterNextSteps && item.key === 'FOLLOW_UP') return false;
      
      // For payment related triggers, only show if template_type is 'ORDER_DETAIL'
      if (['PAYMENT_CAPTURED', 'PAYMENT_FAILED'].includes(item.key)) {
        return template_type === 'ORDER_DETAIL';
      }

      // Filter out FLOW_COMPLETED if not a flow and after next steps
      if (item.key === 'FLOW_COMPLETED' && !isFlow && isAfterNextSteps) {
        return false;
      }

      return true;
    });
  };

  const operationHandlers = {
    // Example structure - you can expand this based on your node types
    default: (operations) => operations.map(item => ({ ...item })),
    // Add more handlers for specific node types as needed
    // exampleNodeType: (operations) => {
    //   // Custom logic for specific node type
    //   return operations.map(item => ({
    //     ...item,
    //     // custom modifications based on node type
    //   }));
    // },
    follow_up: (operations) => operations
      .filter(item => item.key !== 'FOLLOW_UP'),
      
    template: (operations) => filterOperations(operations, isAfterNextSteps),
    
    text: (operations) => filterOperations(operations, isAfterNextSteps),
    
    media: (operations) => filterOperations(operations, isAfterNextSteps),
    
    button: (operations) => filterOperations(operations, isAfterNextSteps),
    
    list: (operations) => filterOperations(operations, isAfterNextSteps),
    
    location_request_message: (operations) => filterOperations(operations, isAfterNextSteps),
    
    cta_url: (operations) => filterOperations(operations, isAfterNextSteps),
    
    contacts: (operations) => filterOperations(operations, isAfterNextSteps),
    
    interactive: (operations) => filterOperations(operations, isAfterNextSteps),
    
  };

  const getProcessedOperations = () => {
    const handler = operationHandlers[nodeType?.toLowerCase()] || operationHandlers.default;
    const operations = handler(OPERATIONS_LIST);
    
    return operations.map(item => {
      if (item.key === 'ASSIGNEE') {
        return {
          ...item,
          isGated: !canUseAutoAssign,
          tooltipMessage: !canUseAutoAssign ? "This feature is not available in your plan. Upgrade to access." : ""
        };
      }
      if (item.key === 'FOLLOW_UP') {
        return {
          ...item,
          isGated: !canUseTimeDelay,
          tooltipMessage: !canUseTimeDelay ? "This feature is not available in your plan. Upgrade to access." : ""
        };
      }
      return item;
    });
  };
  const showAction = (trigger) => {
    const isFollowUpWithLongDelay = nodeType?.toLowerCase() === 'follow_up' && 
      previousNodeType?.data?.operations?.delay > 86400;
    
    const timeDelayPrevNode = findPreviousNode(previousNodeType)
    
    const isGated = messageTypesLimit !== null && messageTypesLimit !== undefined && trigger?.id > messageTypesLimit;

    return {
      ...trigger,
      isGated: isGated,
      tooltipMessage: isGated ? "This message type is not available in your plan. Upgrade to access." : "",
      disabled: isAfterApiTrigger || isFollowUpWithLongDelay || (nodeType?.toLowerCase() === 'follow_up' && timeDelayPrevNode?.is_trigger) ||  timeDelayPrevNode?.data?.type === 'template'
        ? trigger.key !== 'TEMPLATE' 
        : false,
      canAdd: true
    };
  }

  const tabStyle = 'capitalize text-sm weight-medium text-[var(--font-600)]';

  return (
    <>
      {type === 'list' ? (
        <div className='min-w-96'>
          <div className='flex items-center gap-2.5 px-4 pt-4 border-b border-[var(--border-50)]'>
            {['actions', 'operations'].map((tab) => (
              <div
                key={tab}
                className={cn(
                  'py-0.5 px-1 capitalize border-b-2 text-base cursor-pointer text-sm weight-medium',
                  selectedTab === tab
                    ? 'border-[#05805C] text-[#05805C]'
                    : 'border-transparent text-[#898E99]'
                )}
                onClick={() => onTabChange(tab)}
              >
                {tab}
              </div>
            ))}
          </div>
          <div className='p-0'>
            {selectedTab === 'actions' ? (
              <TriggerListContainer
                triggers={ACTIONS_LIST.map(trigger => ({
                  ...trigger,
                  disabled: isAfterApiTrigger ? trigger.key !== 'TEMPLATE' : false
                })).filter(trigger => trigger.key !== 'FLOW' || featureFlags?.whatsapp_flow_feature)}
                onTriggerClick={onTriggerClick}
                allow={allow}
                previousNodeType={previousNodeType}
                findPreviousNode={findPreviousNode}
                isAfterApiTrigger={isAfterApiTrigger}
              />
            ) : (
              <TriggerListContainer
                triggers={getProcessedOperations().filter(operation => operation.key !== 'PAYMENT_CAPTURED' && operation.key !== 'PAYMENT_FAILED' && operation.key !== 'FLOW_COMPLETED')}
                onTriggerClick={onTriggerClick}
                allow={allow}
                previousNodeType={previousNodeType}
                findPreviousNode={findPreviousNode}
                isAfterApiTrigger={isAfterApiTrigger}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'w-fit min-w-[21rem] h-fit absolute bg-white shadow rounded-lg',
            type === 'modal' &&
              `top-[${position.top}px] left-[${position.left}px]`
          )}>
          <div>
            {(edgeFrom !== 'next_steps') && (
              <div className='flex items-center gap-2.5 px-4 pt-4 border-b border-[var(--border-50)]'>
                {tabsList?.map((tab) => (
                  <div
                    key={tab}
                    className={cn(
                      'py-0.5 px-1 capitalize border-b-2 text-base cursor-pointer',
                      tabStyle,
                      selectedTab === tab
                        ? 'border-[#05805C] text-[#05805C]'
                        : 'border-transparent text-[#898E99]'
                    )}
                    onClick={() => onTabChange(tab)}
                  >
                    {tab}
                  </div>
                ))}
              </div>
            )}

            {/*  */}
            <div className=''>
              {selectedTab === 'operations' ? (
                <TriggerListContainer
                  triggers={getProcessedOperations()}
                  onTriggerClick={onTriggerClick}
                  allow={allow}
                  previousNodeType={previousNodeType}
                  findPreviousNode={findPreviousNode}
                  isAfterApiTrigger={isAfterApiTrigger}
                />
              ) : (
                <TriggerListContainer
                  triggers={ACTIONS_LIST.map(trigger => showAction(trigger)).filter(trigger => trigger.key === 'FLOW' && !featureFlags?.whatsapp_flow_feature ? false : true)}
                  onTriggerClick={onTriggerClick}
                  allow={allow}
                  previousNodeType={previousNodeType}
                  findPreviousNode={findPreviousNode}
                  isAfterApiTrigger={isAfterApiTrigger}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
